import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme } from './index';
import { ColorMode } from './tokens';

const STORAGE_KEY = 'deview-color-mode';
const DEFAULT_MODE: ColorMode = 'dark';

interface ColorModeContextValue {
  mode: ColorMode;
  toggleColorMode: () => void;
  setColorMode: (mode: ColorMode) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(
  undefined,
);

const readInitialMode = (): ColorMode => {
  if (typeof window === 'undefined') return DEFAULT_MODE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage unavailable (private mode / SSR) — fall back to default.
  }
  return DEFAULT_MODE;
};

/**
 * Provides the light/dark color mode, persists the choice to localStorage, and
 * wires the matching MUI theme + CssBaseline. Wrap the app once with this and
 * consume the toggle anywhere via `useColorMode`.
 */
export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<ColorMode>(readInitialMode);

  const persist = useCallback((next: ColorMode) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best-effort persistence only.
    }
  }, []);

  const setColorMode = useCallback(
    (next: ColorMode) => {
      setMode(next);
      persist(next);
    },
    [persist],
  );

  const toggleColorMode = useCallback(() => {
    setMode((prev) => {
      const next: ColorMode = prev === 'dark' ? 'light' : 'dark';
      persist(next);
      return next;
    });
  }, [persist]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(
    () => ({ mode, toggleColorMode, setColorMode }),
    [mode, toggleColorMode, setColorMode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useColorMode = (): ColorModeContextValue => {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error('useColorMode must be used within an AppThemeProvider');
  }
  return ctx;
};
